import { useMemo, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { motion as Motion } from "framer-motion";
import { toast } from "react-toastify";

import api from "@/utils/axios";
import i18n from "@/i18n";
import { useAuth } from "@/app/auth-context-state";

import cls from "./CreateMemberSocial.module.scss";

/* Placeholders (q2) */
import mrImg from "@/assets/UI/memberCreate/q19Mr.webp";
import mrMImg from "@/assets/UI/memberCreate/q19MrM.webp";
import mrsImg from "@/assets/UI/memberCreate/q19Mrs.webp";
import mrsMImg from "@/assets/UI/memberCreate/q19MrsM.webp";

/* Framer: same “Welcome” gentle floating (match Avatar) */
const floatSlow = {
  hidden: { opacity: 0.55 },
  show: {
    opacity: [0.55, 0.72, 0.55],
    scale: [1, 1.03, 1],
    transition: { duration: 10, ease: "easeInOut", repeat: Infinity },
  },
};

const floatSlowAlt = {
  hidden: { opacity: 0.55 },
  show: {
    opacity: [0.72, 0.58, 0.72],
    scale: [1.03, 1, 1.03],
    x: [0, -6, 0],
    transition: { duration: 12, ease: "easeInOut", repeat: Infinity },
  },
};

/* Same helper pattern you use across member create */
function useMemberLayoutContext() {
  return useOutletContext();
}

/* Map layout context to persona bucket */
function getPersona(memberType, rawGender) {
  if (memberType === "couple") return "couple";
  if (rawGender === "man" || rawGender === "trans_man") return "man";
  if (rawGender === "woman" || rawGender === "trans_woman") return "woman";
  return "nb";
}

const MIN_SOCIAL_FIELDS = 1;

export default function CreateMemberSocial() {
  const navigate = useNavigate();
  const { refreshMe } = useAuth();

  const layoutCtx = useMemberLayoutContext() || {};
  const { memberType, isCouple: isCoupleCtx, rawGender } = layoutCtx;

  const isCouple = isCoupleCtx || memberType === "couple";
  const persona = getPersona(memberType, rawGender);

  const MrD = useMemo(() => mrImg, []);
  const MrsD = useMemo(() => mrsImg, []);
  const MrM = useMemo(() => mrMImg, []);
  const MrsM = useMemo(() => mrsMImg, []);

  /* ---------- FORM STATE ---------- */
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");

  const [partnerFirstName, setPartnerFirstName] = useState("");
  const [partnerLastName, setPartnerLastName] = useState("");

  const [socialFacebook, setSocialFacebook] = useState("");
  const [socialInstagram, setSocialInstagram] = useState("");
  const [socialTikTok, setSocialTikTok] = useState("");
  const [socialYouTube, setSocialYouTube] = useState("");

  const [submitting, setSubmitting] = useState(false);

  /* ---------- i18n COPY ---------- */
  const title = i18n.t("onboarding.social.title", {
    defaultValue: "Your Real Details (Private Verification Only)",
  });

  const subtitle = i18n.t("onboarding.social.subtitle", {
    defaultValue:
      "Add your real name and at least one real social link. This is private and used only for verification.",
  });

  const step1Title = i18n.t("onboarding.social.step1Title", {
    defaultValue: "Step 1: Your real name",
  });

  const step2Title = i18n.t("onboarding.social.step2Title", {
    defaultValue: "Step 2: Add at least one real social profile",
  });

  const nameIntro =
    persona === "couple"
      ? i18n.t("onboarding.social.couple.nameIntro", {
          defaultValue:
            "For couples, add both names so we can verify you properly.",
        })
      : i18n.t(`onboarding.social.${persona}.nameIntro`, {
          defaultValue:
            "Please enter your real first and last name as it appears on at least one of your socials.",
        });

  const nameHelper =
    persona === "couple"
      ? i18n.t("onboarding.social.couple.nameHelper", {
          defaultValue:
            "Your name + your partner’s name should match at least one of the social profiles below.",
        })
      : i18n.t(`onboarding.social.${persona}.nameHelper`, {
          defaultValue:
            "This is never shown on your profile. It’s only used to confirm you are real.",
        });

  const socialIntro = i18n.t("onboarding.social.socialIntro", {
    defaultValue:
      "Add at least one real social profile link or handle that clearly belongs to you.",
  });

  const socialHelper = i18n.t("onboarding.social.socialHelper", {
    defaultValue:
      "Your socials are private and never displayed on your Noty profile. We only use them to verify authenticity.",
  });

  const labels = {
    firstName: i18n.t("onboarding.social.labels.firstName", {
      defaultValue: "First Name",
    }),
    lastName: i18n.t("onboarding.social.labels.lastName", {
      defaultValue: "Last Name",
    }),
    yourFirstName: i18n.t("onboarding.social.labels.yourFirstName", {
      defaultValue: "Your First Name",
    }),
    yourLastName: i18n.t("onboarding.social.labels.yourLastName", {
      defaultValue: "Your Last Name",
    }),
    partnerFirstName: i18n.t("onboarding.social.labels.partnerFirstName", {
      defaultValue: "Partner First Name",
    }),
    partnerLastName: i18n.t("onboarding.social.labels.partnerLastName", {
      defaultValue: "Partner Last Name",
    }),
    facebook: i18n.t("onboarding.social.labels.facebook", {
      defaultValue: "Facebook URL",
    }),
    instagram: i18n.t("onboarding.social.labels.instagram", {
      defaultValue: "Instagram (URL or @handle)",
    }),
    tiktok: i18n.t("onboarding.social.labels.tiktok", {
      defaultValue: "TikTok (URL or @handle)",
    }),
    youtube: i18n.t("onboarding.social.labels.youtube", {
      defaultValue: "YouTube URL",
    }),
  };

  const errors = {
    needName: i18n.t("onboarding.social.errors.needName", {
      defaultValue: "Please enter your first and last name.",
    }),
    needPartnerName: i18n.t("onboarding.social.errors.needPartnerName", {
      defaultValue: "Please enter both your name and your partner’s name.",
    }),
    needSocial: i18n.t("onboarding.social.errors.needSocial", {
      defaultValue: "Add at least one real social profile to continue.",
    }),
    generic: i18n.t("onboarding.social.errors.generic", {
      defaultValue: "We couldn’t save your details. Please try again.",
    }),
  };

  const ctaSave = i18n.t("common.saveContinue", {
    defaultValue: "Save & Continue",
  });

  const successMessage = i18n.t("onboarding.social.success", {
    defaultValue: "Saved! Next step: payment.",
  });

  /* ---------- SUBMIT ---------- */
  async function handleSubmit(e) {
    e.preventDefault();

    const trimmedFirst = firstName.trim();
    const trimmedLast = lastName.trim();
    const trimmedPartnerFirst = partnerFirstName.trim();
    const trimmedPartnerLast = partnerLastName.trim();

    const socials = [
      socialFacebook.trim(),
      socialInstagram.trim(),
      socialTikTok.trim(),
      socialYouTube.trim(),
    ];
    const filledSocialCount = socials.filter(Boolean).length;

    if (!trimmedFirst || !trimmedLast) {
      toast.info(isCouple ? errors.needPartnerName : errors.needName);
      return;
    }

    if (isCouple && (!trimmedPartnerFirst || !trimmedPartnerLast)) {
      toast.info(errors.needPartnerName);
      return;
    }

    if (filledSocialCount < MIN_SOCIAL_FIELDS) {
      toast.info(errors.needSocial);
      return;
    }

    const payload = {
      firstName: trimmedFirst,
      lastName: trimmedLast,
      socialFacebook: socialFacebook.trim(),
      socialInstagram: socialInstagram.trim(),
      socialTikTok: socialTikTok.trim(),
      socialYouTube: socialYouTube.trim(),
      // extra (safe if backend ignores unknown keys)
      partnerFirstName: trimmedPartnerFirst || undefined,
      partnerLastName: trimmedPartnerLast || undefined,
    };

    try {
      setSubmitting(true);
      const { data } = await api.post("/v1/member-create/social", payload);

      await refreshMe();
      toast.success(successMessage);

      navigate(data?.next || "/member/payment", { replace: true });
    } catch (err) {
      console.error("CreateMemberSocial submit failed:", err);
      toast.error(errors.generic);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className={cls.page}>
      {/* Fixed background stage (Avatar-style) */}
      <section className={cls.heroStage} aria-hidden="true">
        <div className={cls.mobileOnly}>
          <Motion.img
            className={`${cls.heroImg} ${cls.z1}`}
            src={MrM}
            alt=""
            variants={floatSlow}
            initial="hidden"
            animate="show"
            draggable="false"
          />
          <Motion.img
            className={`${cls.heroImg} ${cls.z3}`}
            src={MrsM}
            alt=""
            variants={floatSlowAlt}
            initial="hidden"
            animate="show"
            draggable="false"
          />
        </div>

        <div className={cls.desktopOnly}>
          <Motion.img
            className={`${cls.heroImg} ${cls.z1}`}
            src={MrD}
            alt=""
            variants={floatSlow}
            initial="hidden"
            animate="show"
            draggable="false"
          />
          <Motion.img
            className={`${cls.heroImg} ${cls.z3}`}
            src={MrsD}
            alt=""
            variants={floatSlowAlt}
            initial="hidden"
            animate="show"
            draggable="false"
          />
        </div>

        <div className={cls.stageTint} />
      </section>

      {/* Content layer */}
      <div className={cls.pageInner}>
        <div className={cls.contentShell}>
          <div className={cls.formCard}>
            <div className={cls.formTop}>
              <div className={cls.formMeta}>
                <div className={cls.formTitle}>{title}</div>
                <div className={cls.formSub}>{subtitle}</div>
              </div>
            </div>

            <form className={cls.form} onSubmit={handleSubmit} noValidate>
              {/* STEP 1 */}
              <section className={cls.infoBlock}>
                <div className={cls.infoTitle}>{step1Title}</div>
                <div className={cls.infoNote}>{nameIntro}</div>
                <div className={cls.infoNote}>{nameHelper}</div>
              </section>

              {!isCouple ? (
                <div className={cls.fieldGroup}>
                  <div className={cls.fieldRow}>
                    <label className={cls.fieldLabel} htmlFor="firstName">
                      {labels.firstName}
                    </label>
                    <input
                      id="firstName"
                      className={cls.input}
                      type="text"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      autoComplete="given-name"
                    />
                  </div>

                  <div className={cls.fieldRow}>
                    <label className={cls.fieldLabel} htmlFor="lastName">
                      {labels.lastName}
                    </label>
                    <input
                      id="lastName"
                      className={cls.input}
                      type="text"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      autoComplete="family-name"
                    />
                  </div>
                </div>
              ) : (
                <div className={cls.fieldColumns}>
                  <div className={cls.fieldCol}>
                    <div className={cls.fieldRow}>
                      <label className={cls.fieldLabel} htmlFor="yourFirstName">
                        {labels.yourFirstName}
                      </label>
                      <input
                        id="yourFirstName"
                        className={cls.input}
                        type="text"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        autoComplete="given-name"
                      />
                    </div>

                    <div className={cls.fieldRow}>
                      <label className={cls.fieldLabel} htmlFor="yourLastName">
                        {labels.yourLastName}
                      </label>
                      <input
                        id="yourLastName"
                        className={cls.input}
                        type="text"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        autoComplete="family-name"
                      />
                    </div>
                  </div>

                  <div className={cls.fieldCol}>
                    <div className={cls.fieldRow}>
                      <label
                        className={cls.fieldLabel}
                        htmlFor="partnerFirstName"
                      >
                        {labels.partnerFirstName}
                      </label>
                      <input
                        id="partnerFirstName"
                        className={cls.input}
                        type="text"
                        value={partnerFirstName}
                        onChange={(e) => setPartnerFirstName(e.target.value)}
                        autoComplete="off"
                      />
                    </div>

                    <div className={cls.fieldRow}>
                      <label
                        className={cls.fieldLabel}
                        htmlFor="partnerLastName"
                      >
                        {labels.partnerLastName}
                      </label>
                      <input
                        id="partnerLastName"
                        className={cls.input}
                        type="text"
                        value={partnerLastName}
                        onChange={(e) => setPartnerLastName(e.target.value)}
                        autoComplete="off"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* STEP 2 */}
              <section className={cls.infoBlock}>
                <div className={cls.infoTitle}>{step2Title}</div>
                <div className={cls.infoNote}>{socialIntro}</div>
                <div className={cls.infoNote}>{socialHelper}</div>
              </section>

              <div className={cls.fieldGroup}>
                <div className={cls.fieldRow}>
                  <label className={cls.fieldLabel} htmlFor="socialFacebook">
                    {labels.facebook}
                  </label>
                  <input
                    id="socialFacebook"
                    className={cls.input}
                    type="url"
                    value={socialFacebook}
                    onChange={(e) => setSocialFacebook(e.target.value)}
                    placeholder="https://facebook.com/..."
                  />
                </div>

                <div className={cls.fieldRow}>
                  <label className={cls.fieldLabel} htmlFor="socialInstagram">
                    {labels.instagram}
                  </label>
                  <input
                    id="socialInstagram"
                    className={cls.input}
                    type="text"
                    value={socialInstagram}
                    onChange={(e) => setSocialInstagram(e.target.value)}
                    placeholder="@yourhandle or full URL"
                  />
                </div>

                <div className={cls.fieldRow}>
                  <label className={cls.fieldLabel} htmlFor="socialTikTok">
                    {labels.tiktok}
                  </label>
                  <input
                    id="socialTikTok"
                    className={cls.input}
                    type="text"
                    value={socialTikTok}
                    onChange={(e) => setSocialTikTok(e.target.value)}
                    placeholder="@yourhandle or full URL"
                  />
                </div>

                <div className={cls.fieldRow}>
                  <label className={cls.fieldLabel} htmlFor="socialYouTube">
                    {labels.youtube}
                  </label>
                  <input
                    id="socialYouTube"
                    className={cls.input}
                    type="url"
                    value={socialYouTube}
                    onChange={(e) => setSocialYouTube(e.target.value)}
                    placeholder="https://youtube.com/@..."
                  />
                </div>
              </div>

              {/* Sticky submit (Avatar-style) */}
              <div className={cls.actions}>
                <button
                  type="submit"
                  className={cls.submit}
                  disabled={submitting}
                  aria-busy={submitting ? "true" : "false"}
                >
                  {submitting
                    ? i18n.t("common.saving", { defaultValue: "Saving…" })
                    : ctaSave}
                </button>
              </div>

              {/* Helpful note */}
              <div className={cls.helpNote}>
                <span className={cls.helpDot} aria-hidden="true" />
                {i18n.t("onboarding.social.note", {
                  defaultValue:
                    "Tip: your social can be private. We only check that it’s real and matches your name/photo.",
                })}
              </div>
            </form>
          </div>

          <div className={cls.bottomSpace} />
        </div>
      </div>
    </main>
  );
}
